/* eslint-disable */
import React from 'react';
import SEO from './SEO';
import './styles/styles.css';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Bins Storage – Apartment Storage',
  description:
    'Storage for apartment renters in SF & Tacoma – we pick up at your apartment, store your stuff, and bring it back. No truck, no elevator stress, no wasted weekends.',
  url: 'https://www.bins-storage.com/apartment-storage',
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'San Francisco Bay Area' },
    { '@type': 'City', name: 'Tacoma' },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bins-storage.com/' },
    { '@type': 'ListItem', position: 2, name: 'Apartment Storage', item: 'https://www.bins-storage.com/apartment-storage' },
  ],
};

const apartmentFaqs = [
  {
    q: "I have a tiny SF apartment – will you actually fit down my staircase?",
    a: "Yes. We do SF walk-ups and tight Victorians all day. Our team is trained for it. If we can't fit something (rare), we'll tell you upfront.",
  },
  {
    q: "I'm between leases. Can Bins hold my stuff for two weeks?",
    a: "Yes – that's one of the most common use cases. Store for a week, a month, or longer. No minimums beyond the facility's terms.",
  },
  {
    q: 'Can I add or remove items from storage later?',
    a: 'Yes – schedule a pickup or delivery in the app whenever.',
  },
  {
    q: 'Are moving fees included?',
    a: "Moving fees vary by facility and you'll see them upfront during signup, so there are no surprises.",
  },
  {
    q: 'Can I access my items directly at the facility?',
    a: 'Generally no – we handle all pickups and deliveries to keep your stuff secure. In certain situations we can accommodate it. Contact us.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: apartmentFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const combinedSchema = [localBusinessSchema, breadcrumbSchema, faqSchema];

// ─── Styles ───────────────────────────────────────────────────────────────────

const heroStyle = {
  backgroundColor: '#7B1FA2',
  padding: '4rem 2rem 3rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const heroHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 1rem',
};

const heroSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.2rem',
  color: 'rgba(255,255,255,0.88)',
  maxWidth: 640,
  lineHeight: 1.6,
  margin: '0 0 0.5rem',
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '1.5rem 0 0.75rem',
};

const ctaPrimary = {
  display: 'inline-block',
  backgroundColor: '#FFF',
  color: '#7B1FA2',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
};

const ctaSecondary = {
  display: 'inline-block',
  backgroundColor: 'transparent',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.6)',
};

const trustLine = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.75)',
  margin: '0.5rem 0 0',
};

const sectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 900,
  margin: '0 auto',
};

const sectionHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const cardsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const card = {
  backgroundColor: '#F9F6FC',
  borderRadius: 12,
  padding: '1.75rem 1.5rem',
  textAlign: 'center',
};

const cardTitle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
};

const cardBody = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#555',
  lineHeight: 1.6,
};

const stepNumber = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#7B1FA2',
  marginBottom: '0.5rem',
};

const problemSolutionSection = {
  backgroundColor: '#261136',
  padding: '3.5rem 2rem',
};

const psCard = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1px',
  backgroundColor: 'rgba(255,255,255,0.1)',
  borderRadius: 12,
  overflow: 'hidden',
  marginBottom: '1rem',
};

const psLeft = {
  backgroundColor: '#1e0d2e',
  padding: '1.25rem 1.5rem',
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1rem',
  color: 'rgba(255,255,255,0.7)',
  fontStyle: 'italic',
};

const psRight = {
  backgroundColor: '#2d1248',
  padding: '1.25rem 1.5rem',
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1rem',
  color: '#FFF',
  fontWeight: 600,
};

const unitSizesSection = {
  backgroundColor: '#F9F6FC',
  padding: '3.5rem 2rem',
};

const unitGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1.25rem',
  maxWidth: 860,
  margin: '2rem auto 0',
};

const unitCard = {
  backgroundColor: '#FFF',
  borderRadius: 12,
  padding: '1.5rem',
  border: '1px solid #EDE0F7',
};

const unitSize = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 800,
  fontSize: '1.1rem',
  color: '#7B1FA2',
  marginBottom: '0.4rem',
};

const unitDesc = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.92rem',
  color: '#555',
  lineHeight: 1.55,
};

const pricingSection = {
  backgroundColor: '#7B1FA2',
  padding: '3.5rem 2rem',
  textAlign: 'center',
};

const pricingCtaStyle = {
  display: 'inline-block',
  backgroundColor: '#FFF',
  color: '#7B1FA2',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 28px',
  borderRadius: 999,
  textDecoration: 'none',
};

const faqSectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 720,
  margin: '0 auto',
};

const faqItem = {
  borderBottom: '1px solid #EEE',
  paddingBottom: '1.25rem',
  marginBottom: '1.25rem',
};

const faqQuestion = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#261136',
  marginBottom: '0.4rem',
};

const faqAnswer = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.97rem',
  color: '#555',
  lineHeight: 1.65,
};

const finalCtaSection = {
  backgroundColor: '#261136',
  padding: '4rem 2rem',
  textAlign: 'center',
};

const finalCtaHeading = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 0.75rem',
};

const finalCtaSub = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.8)',
  margin: '0 0 2rem',
  textAlign: 'center',
};

const internalLinksSection = {
  backgroundColor: '#F9F6FC',
  padding: '2rem',
  textAlign: 'center',
};

const internalLinkStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#7B1FA2',
  margin: '0 0.75rem',
  textDecoration: 'underline',
};

// ─── Component ────────────────────────────────────────────────────────────────

const ApartmentStorage = () => (
  <>
    <SEO
      title="Apartment Storage – No Truck, No Hassle | Bins Storage"
      description="Storage for apartment renters in SF & Tacoma – we pick up at your apartment, store your stuff, and bring it back. No truck, no elevator stress, no wasted weekends. Reserve in minutes."
      path="/apartment-storage"
      schema={combinedSchema}
    />

    {/* HERO */}
    <section style={heroStyle}>
      <h1 style={heroHeadingStyle}>Apartment Storage – No Truck Required</h1>
      <p style={heroSubStyle}>
        Tight closets? No garage? No car? Bins picks up at your apartment, stores
        your stuff, and delivers it back – all without you touching a U-Haul.
      </p>
      <div style={ctaRow}>
        <a href="/Download" style={ctaPrimary} data-cta="apt-hero-reserve">
          Reserve Now
        </a>
        <a href="#how-it-works" style={ctaSecondary} data-cta="apt-hero-hiw">
          See How It Works
        </a>
      </div>
      <p style={trustLine}>
        Trusted by apartment renters across SF, Oakland, Berkeley, and Tacoma.
      </p>
    </section>

    {/* BENEFITS */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>Storage Built for Apartment Life</h2>
        <div style={cardsGrid}>
          <div style={card}>
            <div style={cardTitle}>🚫 No Truck, No Tolls, No Gas</div>
            <div style={cardBody}>
              We bring the vehicle and the muscle. You just point.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🏢 Walk-Ups Welcome</div>
            <div style={cardBody}>
              SF walk-up? Third-floor Tacoma apartment? We're trained on stairs,
              tight hallways, and quirky doorways.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>📏 Pay Only for What You Store</div>
            <div style={cardBody}>
              A few boxes, a couch, or a full studio's worth – pick the unit size
              that fits.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PROBLEM / SOLUTION */}
    <section style={problemSolutionSection}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h2 style={{ fontFamily: 'Raleway, sans-serif', fontSize: '2rem', fontWeight: 700, color: '#FFF', textAlign: 'center', marginBottom: '2rem' }}>
          Sound Familiar?
        </h2>
        {[
          { p: '"I don\'t have a car."', s: 'Good – you don\'t need one. We drive.' },
          { p: '"My building doesn\'t have a loading zone."', s: 'We work around it. Our team finds the right spot and the right moment.' },
          { p: '"I don\'t want to give up my weekend hauling boxes."', s: 'Don\'t. We do it in an hour or two while you do anything else.' },
        ].map(({ p, s }) => (
          <div key={p} style={psCard}>
            <div style={psLeft}>{p}</div>
            <div style={psRight}>{s}</div>
          </div>
        ))}
      </div>
    </section>

    {/* UNIT SIZES */}
    <section style={unitSizesSection}>
      <h2 style={sectionHeadingStyle}>What Fits in a Unit</h2>
      <div style={unitGrid}>
        {[
          { size: '5×5 (Small)', desc: '10–15 boxes, ski gear, bike, seasonal clothing' },
          { size: '5×10 (Medium)', desc: "Studio apartment's worth – bed, dresser, boxes, bike" },
          { size: '10×10 (Large)', desc: '1-bedroom apartment – sofa, queen bed, dining table, boxes' },
          { size: '10×15+ (Extra-Large)', desc: '2-bedroom apartment' },
        ].map(({ size, desc }) => (
          <div key={size} style={unitCard}>
            <div style={unitSize}>{size}</div>
            <div style={unitDesc}>{desc}</div>
          </div>
        ))}
      </div>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '0.95rem', color: '#666', textAlign: 'center', marginTop: '1.5rem' }}>
        See exact sizing and pricing at your local facility on the{' '}
        <a href="/Download" style={{ color: '#7B1FA2' }}>reserve page</a>.
      </p>
    </section>

    {/* HOW IT WORKS */}
    <section id="how-it-works" style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>How Bins Works for Apartment Renters</h2>
        <div style={cardsGrid}>
          {[
            { n: '1', t: 'Reserve online', b: 'Pick your facility, unit size, and a pickup time.' },
            { n: '2', t: 'We come to your apartment', b: 'Elevators, walk-ups, tight hallways – our team handles it.' },
            { n: '3', t: 'We store', b: 'Climate-controlled facility, 24/7 monitored, insured.' },
            { n: '4', t: 'We deliver back', b: 'Schedule individual items or the whole unit when you\'re ready.' },
          ].map(({ n, t, b }) => (
            <div key={n} style={card}>
              <div style={stepNumber}>{n}</div>
              <div style={cardTitle}>{t}</div>
              <div style={cardBody}>{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section style={pricingSection}>
      <h2 style={{ fontFamily: 'Raleway, sans-serif', fontSize: '2rem', fontWeight: 700, color: '#FFF', marginBottom: '0.75rem' }}>
        Apartment Storage Pricing
      </h2>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.85)', maxWidth: 560, margin: '0 auto 2rem', lineHeight: 1.6 }}>
        Pricing depends on your facility and unit size. Compare all options on the
        reserve page before you commit – no surprises.
      </p>
      <a href="/Download" style={pricingCtaStyle} data-cta="apt-pricing-reserve">
        Compare Pricing →
      </a>
    </section>

    {/* FAQ */}
    <section style={faqSectionStyle}>
      <h2 style={{ ...sectionHeadingStyle, textAlign: 'left', marginBottom: '1.75rem' }}>
        Apartment Storage FAQ
      </h2>
      {apartmentFaqs.map(({ q, a }) => (
        <div key={q} style={faqItem}>
          <div style={faqQuestion}>{q}</div>
          <div style={faqAnswer}>{a}</div>
        </div>
      ))}
    </section>

    {/* FINAL CTA */}
    <section style={finalCtaSection}>
      <h2 style={finalCtaHeading}>Stop Paying for Square Footage You Don't Use.</h2>
      <p style={finalCtaSub}>Reserve your Bins unit in minutes. No truck. No hassle.</p>
      <a href="/Download" style={{ ...ctaPrimary, backgroundColor: '#7B1FA2', color: '#FFF' }} data-cta="apt-final-reserve">
        Reserve Now
      </a>
    </section>

    {/* INTERNAL LINKS */}
    <section style={internalLinksSection}>
      <p style={{ fontFamily: 'Raleway, sans-serif', color: '#666', fontSize: '0.9rem', margin: '0 0 0.75rem', textAlign: 'center' }}>
        Explore more
      </p>
      <a href="/san-francisco" style={internalLinkStyle}>SF Bay Area Storage</a>
      <a href="/tacoma" style={internalLinkStyle}>Tacoma Storage</a>
      <a href="/students" style={internalLinkStyle}>Student Storage</a>
      <a href="/FAQ" style={internalLinkStyle}>FAQ</a>
      <a href="/About" style={internalLinkStyle}>About</a>
    </section>
  </>
);

export default ApartmentStorage;
